import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial session + listener
  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
    });
    return () => {
      mounted = false;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  // Utility to fetch clinic data for a user
  const fetchClinicData = async (userId) => {
    // 1. Try clinic_members
    const { data: members } = await supabase
      .from("clinic_members")
      .select("clinic_id, role, clinic:clinics(*)")
      .eq("profile_id", userId);

    if (members && members.length > 0) {
      // Priorizar a role que tem maior privilégio: 'admin' > 'receptionist' > 'professional'
      let selectedMem = members[0];
      const hasAdmin = members.find(m => m.role === "admin" || m.role === "owner");
      const hasReceptionist = members.find(m => m.role === "receptionist");
      
      if (hasAdmin) {
        selectedMem = hasAdmin;
      } else if (hasReceptionist) {
        selectedMem = hasReceptionist;
      }
      
      return { ...selectedMem.clinic, my_role: selectedMem.role };
    }

    // 2. Fallback: Check if user is owner of any clinic
    const { data: owned } = await supabase
      .from("clinics")
      .select("*")
      .eq("owner_id", userId)
      .limit(1)
      .maybeSingle();

    if (owned) {
      return { ...owned, my_role: "admin" };
    }

    return null;
  };

  // Load profile when session changes
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!session?.user?.id) {
        setProfile(null);
        setClinic(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      
      const [pRes, clinicData] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, role, full_name, email, phone, avatar_url, created_at, is_approved, subscription_status, is_blocked, subscription_expires_at, system_plan_id, custom_max_professionals, custom_max_secretaries")
          .eq("id", session.user.id)
          .maybeSingle(),
        fetchClinicData(session.user.id)
      ]);

      if (cancelled) return;
      
      if (pRes.error) console.error("profile load error:", pRes.error);
      setProfile(pRes.data || null);
      setClinic(clinicData);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [session?.user?.id]);

  const value = useMemo(() => ({
    session,
    user: session?.user || null,
    profile,
    clinic,
    clinicId: clinic?.id || null,
    role: profile?.role || null,
    loading,

    async signIn({ email, password }) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return data;
    },

    async signUp({ email, password, full_name, role, system_plan_id }) {
      const { data, error } = await supabase.auth.signUp({
        email, password,
        options: { 
          data: { 
            full_name, 
            role,
            system_plan_id: system_plan_id || 'solo' 
          } 
        },
      });
      if (error) throw error;
      return data;
    },

    async signOut() {
      await supabase.auth.signOut();
      setProfile(null);
      setClinic(null);
    },

    refreshProfile: async () => {
      if (!session?.user?.id) return;
      const [pRes, clinicData] = await Promise.all([
        supabase.from("profiles").select("*").eq("id", session.user.id).maybeSingle(),
        fetchClinicData(session.user.id)
      ]);
      setProfile(pRes.data || null);
      setClinic(clinicData);
    },
  }), [session, profile, clinic, loading]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
